/**
 * Social Feed service (SOCIAL_LAYER_PLAN §A / P2).
 *
 * Thin TanStack-Query wrappers over the governance-service social-feed API.
 * Every path is unversioned (`/feed`, `/social/*`) and resolves to
 * governance-service via the PATH_SERVICE_MAP governance fallback — ZERO gateway
 * change. Byte-identical across frontend-client and frontend-adminclient.
 */
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query'
import { get, post, del, uploadFile } from '@/lib/api/client'
import type {
  FeedScope, PostType, FeedNature, FeedSort, NatureFilter,
} from '@/pages/feed/feed-utils'

// ── Types ────────────────────────────────────────────────────────────────────
export interface FeedAuthor {
  tenant_id?: string
  user_id?: string
  name?: string
  kind?: 'business' | 'client'
}
export interface FeedMedia { media_id?: string; public_url?: string; kind?: string }
export interface FeedEvent {
  starts_at?: string; ends_at?: string; location?: string; is_online?: boolean; url?: string
}
export interface FeedCounts {
  reactions: number; comments: number; reshares: number; external?: number
}

/** A poll (sondage) option with its live vote count. */
export interface PollOption { id: string; label: string; votes?: number }
export interface FeedPoll {
  question?: string
  options?: PollOption[]
  multi?: boolean
  closes_at?: string
  total_votes?: number
  my_vote?: string | null
}

/** The live-resolved entity referenced by a nature='share' element. */
export interface SharedEntity {
  type?: string | null
  type_label?: string | null
  icon?: string | null
  ref_id?: string | null
  available: boolean
  title?: string | null
  subtitle?: string | null
  route?: string | null
}

export interface FeedCard {
  id: string
  kind: 'post' | 'projection'
  /** 'post' = free content · 'share' = reference to a real entity / reshare */
  nature?: FeedNature
  source?: { type?: string; id?: string }
  post_type: PostType
  author: FeedAuthor
  title?: string | null
  body?: string | null
  media: FeedMedia[]
  link_url?: string | null
  event?: FeedEvent
  tags?: string[]
  poll?: FeedPoll
  /** marketplace share (nature='share') */
  share_type?: string | null
  share_ref_id?: string | null
  share_comment?: string | null
  comment?: string | null
  shared_entity?: SharedEntity
  /** internal reshare: the original post embedded */
  reshare_of?: string | null
  reshared_post?: FeedCard | null
  audience_scope?: string
  status?: string
  moderation_status?: string
  pinned?: boolean
  source_ref?: Record<string, unknown>
  extra?: Record<string, unknown>
  /** type-specific optional fields (deal / financement / info / …) */
  config?: Record<string, unknown>
  counts: FeedCounts
  created_at?: string
  published_at?: string
}

export interface FeedResponse {
  tenant_id: string
  scope: string
  items: FeedCard[]
  count: number
  next_cursor: string | null
  has_more: boolean
}

export interface FeedComment {
  id: string
  post_id: string
  user_id?: string
  name?: string
  body?: string
  parent_comment_id?: string | null
  created_at?: string
}

export interface CreatePostInput {
  nature?: FeedNature
  post_type: PostType
  title?: string
  body?: string
  media?: FeedMedia[]
  link_url?: string
  event?: FeedEvent
  tags?: string[]
  poll?: { question: string; options: { id?: string; label: string }[]; multi?: boolean }
  /** type-specific optional fields (deal / financement / info / …) */
  config?: Record<string, unknown>
  audience_scope?: 'global' | 'clients' | 'followers'
  author_kind?: 'business' | 'client'
}

export interface ShareableType {
  code: string
  label?: string
  description?: string
  icon?: string
  sensitivity?: 'public' | 'internal'
  max_scope?: 'global' | 'clients' | 'followers'
}
export interface ShareableEntity {
  ref_id: string
  title?: string
  subtitle?: string
  created_at?: string
}
export interface CreateShareInput {
  share_type: string
  entite_ref_id: string
  comment?: string
  audience_scope?: 'global' | 'clients' | 'followers'
  author_kind?: 'business' | 'client'
}

// ── Query keys ───────────────────────────────────────────────────────────────
export interface FeedFilters {
  type?: string
  sort?: FeedSort
  nature?: NatureFilter
  shareType?: string
}
export const feedKeys = {
  all: ['social-feed'] as const,
  timeline: (scope: FeedScope, f?: FeedFilters) =>
    ['social-feed', 'timeline', scope, f?.type ?? 'all', f?.sort ?? 'recent',
      f?.nature ?? 'all', f?.shareType ?? 'all'] as const,
  comments: (postId: string) => ['social-feed', 'comments', postId] as const,
  reactions: (postId: string) => ['social-feed', 'reactions', postId] as const,
  shareableTypes: ['social-feed', 'shareable-types'] as const,
  shareableEntities: (type: string, q?: string) =>
    ['social-feed', 'shareable-entities', type, q ?? ''] as const,
  moderation: (status?: string) => ['social-feed', 'moderation', status ?? 'open'] as const,
  audit: (postId: string) => ['social-feed', 'moderation-audit', postId] as const,
  follows: ['social-feed', 'follows'] as const,
  unread: ['social-feed', 'unread-head'] as const,
}

const PAGE_LIMIT = 12

// ── Timeline (infinite) ──────────────────────────────────────────────────────
export function useFeed(scope: FeedScope, filters?: FeedFilters) {
  return useInfiniteQuery({
    queryKey: feedKeys.timeline(scope, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      get<FeedResponse>('/feed', {
        params: {
          scope,
          type: filters?.type || undefined,
          sort: filters?.sort && filters.sort !== 'recent' ? filters.sort : undefined,
          nature: filters?.nature && filters.nature !== 'all' ? filters.nature : undefined,
          share_type: filters?.shareType || undefined,
          cursor: pageParam || undefined,
          limit: PAGE_LIMIT,
        },
      }),
    getNextPageParam: (last) => (last?.has_more ? last?.next_cursor || undefined : undefined),
    staleTime: 20_000,
    refetchOnWindowFocus: true,
    // Live-ish: keep the top of the feed fresh without a WS dependency.
    refetchInterval: scope === 'for-you' || scope === 'global' ? 60_000 : false,
  })
}

// ── Marketplace sharing ──────────────────────────────────────────────────────
export function useShareableTypes() {
  return useQuery({
    queryKey: feedKeys.shareableTypes,
    queryFn: () => get<{ items: ShareableType[]; deferred: ShareableType[] }>('/social/shareable-types'),
    staleTime: 300_000,
  })
}
export function useShareableEntities(type: string | undefined, q: string, enabled: boolean) {
  return useQuery({
    queryKey: feedKeys.shareableEntities(type ?? '', q),
    enabled: enabled && !!type,
    queryFn: () =>
      get<{ items: ShareableEntity[]; label?: string; total: number }>('/social/shareable-entities', {
        params: { type, q: q || undefined, limit: 40 },
      }),
    staleTime: 30_000,
  })
}
export interface CreateShareResult {
  id: string; persisted: boolean; audience_scope: string; scope_clamped: boolean; post?: FeedCard
}
export function useCreateShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateShareInput) => post<CreateShareResult>('/social/shares', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedKeys.all }),
  })
}

/** Flatten infinite pages into a single card list. */
export function flattenFeed(pages?: { items: FeedCard[] }[]): FeedCard[] {
  if (!pages) return []
  return pages.flatMap((p) => p.items || [])
}

// ── Create / delete post ─────────────────────────────────────────────────────
export function useCreatePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePostInput) =>
      post<{ id: string; post: FeedCard; persisted: boolean }>('/social/posts', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.all })
    },
  })
}

export function useDeletePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (postId: string) => del(`/social/posts/${postId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedKeys.all }),
  })
}

// ── Interactions ─────────────────────────────────────────────────────────────
export interface ReactResult { reaction_count: number; persisted?: boolean }
export function reactToPost(postId: string, reaction_type = 'like') {
  return post<ReactResult>(`/social/posts/${postId}/react`, { reaction_type })
}
export function unreactToPost(postId: string, reaction_type = 'like') {
  return del<ReactResult>(`/social/posts/${postId}/react`, { params: { reaction_type } })
}
/** Remove ALL of the caller's reactions on a post (used when switching type). */
export function clearMyReactions(postId: string) {
  return del<ReactResult>(`/social/posts/${postId}/react`)
}
export interface BookmarkResult { bookmarked: boolean }
export function toggleBookmark(postId: string) {
  return post<BookmarkResult>(`/social/posts/${postId}/bookmark`, {})
}
export interface ReshareResult {
  reshare_count: number; reshare_id: string; reshare_element_id?: string
  audience_scope?: string; scope_clamped?: boolean
}
export function resharePost(postId: string, comment?: string, authorKind: 'business' | 'client' = 'client') {
  return post<ReshareResult>(`/social/posts/${postId}/reshare`, {
    comment: comment || undefined, author_kind: authorKind,
  })
}

// ── Reactions (typed) ────────────────────────────────────────────────────────
export interface ReactionsSummary {
  by_type: Record<string, number>; total: number; my_reaction: string | null
}
export function useReactionsSummary(postId: string, enabled: boolean) {
  return useQuery({
    queryKey: feedKeys.reactions(postId),
    enabled,
    queryFn: () => get<ReactionsSummary>(`/social/posts/${postId}/reactions`),
    staleTime: 15_000,
  })
}

// ── Poll (sondage) ───────────────────────────────────────────────────────────
export function votePoll(postId: string, optionId: string) {
  return post<{ poll: FeedPoll; persisted: boolean }>(
    `/social/posts/${postId}/poll-vote`, { option_id: optionId })
}

// ── External share (Open Graph) ──────────────────────────────────────────────
export interface OpenGraph {
  title?: string; description?: string; url?: string; image?: string | null; site_name?: string
}
export function externalShare(postId: string, channel = 'link') {
  return post<{ open_graph: OpenGraph; external_share_count?: number }>(
    `/social/posts/${postId}/external-share`,
    { channel, origin: typeof window !== 'undefined' ? window.location.origin : undefined },
  )
}

// ── Moderation ───────────────────────────────────────────────────────────────
export function reportPost(postId: string, reason: string, detail?: string) {
  return post<{ report_id: string; persisted: boolean }>(
    `/social/posts/${postId}/report`, { reason, detail: detail || undefined })
}
export interface ModerationReport {
  id: string; post_id: string; reason: string; detail?: string | null; status: string
  reporter_name?: string | null; created_at?: string; reviewed_at?: string | null
  resolution?: string | null; post_title?: string | null; post_body?: string | null
  post_nature?: string | null; post_author?: string | null; moderation_status?: string | null
}
export function useModerationQueue(status = 'open') {
  return useQuery({
    queryKey: feedKeys.moderation(status),
    queryFn: () => get<{ items: ModerationReport[]; total: number; open: number }>(
      '/social/moderation/queue', { params: { status } }),
    staleTime: 15_000,
  })
}
export function useRemovePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ postId, reason }: { postId: string; reason?: string }) =>
      post(`/social/moderation/${postId}/remove`, { reason: reason || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.all })
      qc.invalidateQueries({ queryKey: ['social-feed', 'moderation'] })
    },
  })
}
export function useDismissReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => post(`/social/moderation/reports/${reportId}/dismiss`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-feed', 'moderation'] }),
  })
}
export interface ModerationAuditEntry {
  id: string; post_id: string; actor_name?: string | null; action: string
  detail?: string | null; created_at?: string
}
export function useModerationAudit(postId: string, enabled: boolean) {
  return useQuery({
    queryKey: feedKeys.audit(postId),
    enabled,
    queryFn: () => get<{ items: ModerationAuditEntry[]; total: number }>(
      `/social/moderation/${postId}/audit`),
    staleTime: 15_000,
  })
}

// ── Comments ─────────────────────────────────────────────────────────────────
export function useComments(postId: string, enabled: boolean) {
  return useQuery({
    queryKey: feedKeys.comments(postId),
    enabled,
    queryFn: () =>
      get<{ items: FeedComment[]; total: number }>(`/social/posts/${postId}/comments`, {
        params: { limit: 200 },
      }),
    staleTime: 15_000,
  })
}
export function useAddComment(postId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      post<{ comment_id: string; comment_count: number }>(
        `/social/posts/${postId}/comments`, { body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedKeys.comments(postId) }),
  })
}

// ── Follows ──────────────────────────────────────────────────────────────────
export interface FollowRow { id: string; business_tenant_id: string; created_at?: string }
export function useFollows() {
  return useQuery({
    queryKey: feedKeys.follows,
    queryFn: () => get<{ items: FollowRow[] }>('/social/follows'),
    staleTime: 60_000,
  })
}
export function useFollowBusiness() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (business_tenant_id: string) =>
      post('/social/follows', { business_tenant_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.follows })
      qc.invalidateQueries({ queryKey: feedKeys.all })
    },
  })
}
export function useUnfollowBusiness() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (business_tenant_id: string) =>
      del(`/social/follows/${business_tenant_id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.follows })
      qc.invalidateQueries({ queryKey: feedKeys.all })
    },
  })
}

// ── Media upload ─────────────────────────────────────────────────────────────
export interface UploadedMedia {
  media_id: string
  public_url?: string
  url?: string
  kind?: string
}
export async function uploadFeedMedia(file: File): Promise<FeedMedia> {
  const res = await uploadFile<UploadedMedia>('/social/media', file)
  return {
    media_id: res.media_id,
    public_url: res.public_url || res.url,
    kind: res.kind || (file.type.startsWith('video/') ? 'video' : 'image'),
  }
}

// ── Top-bar unread indicator (lightweight, limit=1) ──────────────────────────
const LAST_SEEN_KEY = 'sgrc-feed-last-seen'
export function markFeedSeen() {
  try { localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString()) } catch { /* noop */ }
}
export function useFeedUnread() {
  return useQuery({
    queryKey: feedKeys.unread,
    queryFn: async () => {
      const res = await get<FeedResponse>('/feed', { params: { scope: 'for-you', limit: 1 } })
      const newest = res.items?.[0]?.created_at || null
      let lastSeen: string | null = null
      try { lastSeen = localStorage.getItem(LAST_SEEN_KEY) } catch { /* noop */ }
      return { newest, hasNew: !!newest && (!lastSeen || newest > lastSeen) }
    },
    staleTime: 30_000,
    refetchInterval: 90_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
}
